import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import { useLang } from "@/i18n";

export default function Contact() {
  const { t } = useLang();

  return (
    <section id="contact" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#FAF8F4] via-[#F0ECE4]/60 to-[#FAF8F4]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow" data-testid="contact-eyebrow">{t.contact.eyebrow}</p>
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-[#3B322A] sm:text-4xl" data-testid="contact-title">
            {t.contact.title1}
            <span className="gold-gradient-text italic">{t.contact.titleAccent}</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#5F5647]">
            {t.contact.sub}
          </p>
        </div>

        <div className="mx-auto mt-14 grid max-w-4xl gap-6 md:grid-cols-2">
          <div className="card-lux rounded-md p-7" data-testid="contact-info-card">
            <h3 className="font-display text-xl font-semibold text-[#6E4E2E]">{t.contact.infoTitle}</h3>
            <ul className="mt-6 space-y-5 text-sm">
              <li className="flex items-start gap-3.5">
                <Mail size={18} className="mt-0.5 shrink-0 text-[#A87C4F]" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#948678]">{t.contact.infoLabels.email}</p>
                  <a href="mailto:valeriadragneva@gmail.com" data-testid="contact-email-link"
                    className="mt-1 block text-[#3B322A] transition-colors hover:text-[#856237]">
                    valeriadragneva@gmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3.5">
                <Phone size={18} className="mt-0.5 shrink-0 text-[#A87C4F]" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#948678]">{t.contact.infoLabels.phone}</p>
                  <a href="tel:+33662805612" data-testid="contact-phone-value"
                    className="mt-1 block text-[#3B322A] transition-colors hover:text-[#856237]">
                    +33 6 62 80 56 12
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3.5">
                <MapPin size={18} className="mt-0.5 shrink-0 text-[#A87C4F]" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#948678]">{t.contact.infoLabels.location}</p>
                  <p className="mt-1 text-[#3B322A]">{t.contact.location}</p>
                </div>
              </li>
              <li className="flex items-start gap-3.5">
                <Facebook size={18} className="mt-0.5 shrink-0 text-[#A87C4F]" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#948678]">{t.contact.infoLabels.facebook}</p>
                  <a href="https://www.facebook.com/ebshotelsrevenue" target="_blank" rel="noopener noreferrer"
                    data-testid="facebook-social-link"
                    className="mt-1 block text-[#3B322A] transition-colors hover:text-[#856237]">
                    facebook.com/ebshotelsrevenue
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3.5">
                <Instagram size={18} className="mt-0.5 shrink-0 text-[#A87C4F]" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#948678]">{t.contact.infoLabels.instagram}</p>
                  <a href="https://www.instagram.com/valeriadragneva.consulting" target="_blank" rel="noopener noreferrer"
                    data-testid="instagram-social-link"
                    className="mt-1 block text-[#3B322A] transition-colors hover:text-[#856237]">
                    @valeriadragneva.consulting
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3.5">
                <Linkedin size={18} className="mt-0.5 shrink-0 text-[#A87C4F]" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#948678]">LinkedIn</p>
                  <a href="https://www.linkedin.com/in/valeriadragneva" target="_blank" rel="noopener noreferrer"
                    data-testid="linkedin-social-link"
                    className="mt-1 block text-[#3B322A] transition-colors hover:text-[#856237]">
                    Valeria Dragneva
                  </a>
                </div>
              </li>
            </ul>
          </div>

          <div className="card-lux rounded-md p-7" data-testid="contact-engagements-card">
            <p className="font-mono-eyebrow text-[10px] uppercase tracking-[0.24em] text-[#948678]">
              {t.contact.engagementsTitle}
            </p>
            <ul className="mt-5 space-y-3.5 text-sm text-[#5F5647]">
              {t.contact.engagements.map((e) => (
                <li key={e} className="flex gap-3"><span className="text-[#A87C4F]">◆</span> {e}</li>
              ))}
            </ul>
            <a
              href="mailto:valeriadragneva@gmail.com?subject=Demande%20de%20contact%20—%20EBS%20Hotels%20Revenue"
              data-testid="contact-email-cta-button"
              className="btn-gold mt-8 inline-flex items-center gap-2 rounded-sm px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em]"
            >
              <Mail size={16} />
              {t.contact.infoLabels.email}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
